import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = [
  ['IGG', 'Investigative Genetic Genealogy (IGG)'],
  ['DNA_CODIS', 'CODIS STR / Direct DNA Match'],
  ['FINGERPRINTS', 'Fingerprints'],
  ['DENTAL', 'Dental Comparison'],
  ['VISUAL', 'Visual Identification'],
  ['OTHER', 'Other'],
];

function authHeaders() {
  const t = localStorage.getItem('access_token')
    || localStorage.getItem('session_token')
    || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function MarkIdentifiedDialog({
  caseId, caseName, open, onOpenChange, onResolved,
}) {
  const [form, setForm] = useState({
    identified_name: '',
    identified_age: '',
    identified_origin: '',
    identified_missing_date: '',
    identification_method: 'IGG',
    identification_lab: '',
    identification_agency: '',
    identification_agency_contact: '',
    announcement_date: new Date().toISOString().slice(0, 10),
    source_url: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.identified_name.trim()) {
      toast.error('Identified name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.identified_age) {
        payload.identified_age = parseInt(payload.identified_age, 10);
      } else {
        delete payload.identified_age;
      }
      const res = await fetch(
        `${API}/admin/mark-identified/${caseId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
          body: JSON.stringify(payload),
        });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'mark-identified failed');
      }
      setResult(data);
      toast.success(`${data.identified_name} added to resolved cases`, {
        description: `${data.matches_deactivated} matches deactivated · `
          + `${data.outreach_cancelled} outreach cancelled`,
      });
      onResolved?.(data);
    } catch (err) {
      toast.error(`Mark identified failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="mark-identified-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Mark Case Identified
          </DialogTitle>
          <DialogDescription>
            {caseName && (
              <>
                <span className="font-semibold">{caseName}</span> — this
                workflow marks all linked records as identified, deactivates
                active case_matches, cancels pending outreach letters, and
                inserts a row in the public <code>/resolved</code> timeline.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="rounded-lg border border-emerald-200
                          bg-emerald-50 p-4 space-y-2">
            <div className="flex items-center gap-2 font-semibold
                            text-emerald-900">
              <CheckCircle2 className="w-5 h-5" />
              {result.identified_name} — identification recorded
            </div>
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-emerald-700">Year resolved:</dt>
              <dd className="text-emerald-900 font-medium">{result.resolved_year}</dd>
              <dt className="text-emerald-700">Records updated:</dt>
              <dd className="text-emerald-900 font-medium tabular-nums">{result.records_updated}</dd>
              <dt className="text-emerald-700">Matches deactivated:</dt>
              <dd className="text-emerald-900 font-medium tabular-nums">{result.matches_deactivated}</dd>
              <dt className="text-emerald-700">Outreach cancelled:</dt>
              <dd className="text-emerald-900 font-medium tabular-nums">{result.outreach_cancelled}</dd>
              <dt className="text-emerald-700">Years unidentified:</dt>
              <dd className="text-emerald-900 font-medium tabular-nums">{result.years_unidentified ?? '—'}</dd>
            </dl>
            <div className="pt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                data-testid="mark-identified-close">
                Close
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('/resolved', '_blank')}
                data-testid="mark-identified-open-resolved">
                Open public /resolved timeline
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50
                            p-2 text-xs text-amber-900">
              <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
              Only submit verified identifications sourced from the
              investigating agency or DNA lab. This action is reversible
              but public once the <code>/resolved</code> page is refreshed.
            </div>

            <div>
              <Label>Identified name *</Label>
              <Input
                value={form.identified_name}
                onChange={set('identified_name')}
                placeholder="Victoria Jean Hargrove"
                data-testid="mi-field-name" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Age at death / disappearance</Label>
                <Input
                  type="number"
                  value={form.identified_age}
                  onChange={set('identified_age')}
                  data-testid="mi-field-age" />
              </div>
              <div>
                <Label>Missing-from date</Label>
                <Input
                  type="date"
                  value={form.identified_missing_date}
                  onChange={set('identified_missing_date')}
                  data-testid="mi-field-missing-date" />
              </div>
            </div>

            <div>
              <Label>Origin (city, state)</Label>
              <Input
                value={form.identified_origin}
                onChange={set('identified_origin')}
                placeholder="Opelika, Alabama"
                data-testid="mi-field-origin" />
            </div>

            <div>
              <Label>Identification method *</Label>
              <Select
                value={form.identification_method}
                onValueChange={(v) =>
                  setForm({ ...form, identification_method: v })}>
                <SelectTrigger data-testid="mi-field-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Lab</Label>
                <Input
                  value={form.identification_lab}
                  onChange={set('identification_lab')}
                  placeholder="California DOJ"
                  data-testid="mi-field-lab" />
              </div>
              <div>
                <Label>Announcement date</Label>
                <Input
                  type="date"
                  value={form.announcement_date}
                  onChange={set('announcement_date')}
                  data-testid="mi-field-announce" />
              </div>
            </div>

            <div>
              <Label>Announcing agency</Label>
              <Input
                value={form.identification_agency}
                onChange={set('identification_agency')}
                placeholder="Riverside County DA Cold Case Homicide Team"
                data-testid="mi-field-agency" />
            </div>

            <div>
              <Label>Agency contact (investigator · phone · email)</Label>
              <Input
                value={form.identification_agency_contact}
                onChange={set('identification_agency_contact')}
                placeholder="Investigator Billy Hester · (951) 955-0070 · coldcaseunit@rivcoda.org"
                data-testid="mi-field-agency-contact" />
            </div>

            <div>
              <Label>Source URL</Label>
              <Input
                value={form.source_url}
                onChange={set('source_url')}
                placeholder="https://rivcoda.org/victoria_hargrove_cold_case"
                data-testid="mi-field-source-url" />
            </div>

            <div>
              <Label>Notes (optional — public-facing)</Label>
              <Textarea
                value={form.notes}
                onChange={set('notes')}
                rows={2}
                data-testid="mi-field-notes" />
            </div>

            <div className="pt-2 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="mark-identified-cancel">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="mark-identified-submit">
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Recording…</>
                  : 'Mark identified + resolve case'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
